import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Briefcase, Plus, Search, X, Building2, Users, Calendar,
  ChevronRight, Loader2, TrendingUp, ArrowUpRight
} from "lucide-react";
import {
  DEAL_STATUSES, DEAL_STATUS_LABELS, DEAL_STATUS_COLORS,
  PRIORITIES, PRIORITY_LABELS, PRIORITY_COLORS
} from "../lib/constants";

const emptyDeal = { title: "", company_id: "", primary_contact_id: "", status: "new_lead", value_estimate: 0, priority: "medium", source: "", next_action_date: "", notes: "" };

export default function DealsPage() {
  const { token } = useAuth();
  const [deals, setDeals] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ ...emptyDeal });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [dRes, cRes, coRes] = await Promise.all([
        axios.get(`${API}/deals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/companies`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/contacts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setDeals(dRes.data);
      setCompanies(cRes.data);
      setContacts(coRes.data);
    } catch { toast.error("Failed to load deals"); }
    finally { setLoading(false); }
  };

  const filtered = deals.filter(d => {
    if (search && !d.title.toLowerCase().includes(search.toLowerCase()) && !(d.company_name || "").toLowerCase().includes(search.toLowerCase())) return false;
    if (filterStatus && d.status !== filterStatus) return false;
    if (filterPriority && d.priority !== filterPriority) return false;
    return true;
  });

  const totalValue = filtered.reduce((s, d) => s + (d.value_estimate || 0), 0);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, company_id: form.company_id || null, primary_contact_id: form.primary_contact_id || null, value_estimate: parseFloat(form.value_estimate) || 0, next_action_date: form.next_action_date || null };
      await axios.post(`${API}/deals`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Deal created");
      setShowCreate(false);
      setForm({ ...emptyDeal });
      fetchData();
    } catch { toast.error("Failed to create deal"); }
    finally { setSaving(false); }
  };

  const companyContacts = form.company_id ? contacts.filter(c => c.company_id === form.company_id) : contacts;

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#C9A96E]" /></div>;

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-white">Deals</h1>
          <p className="text-white/40 text-sm mt-1">{deals.length} deals · Pipeline value: <span className="text-[#C9A96E] font-mono">{"\u20AC"}{totalValue.toLocaleString()}</span></p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-accent flex items-center gap-2" data-testid="create-deal-btn">
          <Plus className="w-4 h-4" /> New Deal
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search deals..." className="form-input pl-10 w-full" data-testid="deals-search" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="form-input w-auto min-w-[160px]">
          <option value="">All Statuses</option>
          {DEAL_STATUSES.map(s => <option key={s} value={s}>{DEAL_STATUS_LABELS[s]}</option>)}
        </select>
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="form-input w-auto min-w-[120px]">
          <option value="">All Priorities</option>
          {PRIORITIES.map(p => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
        </select>
      </div>

      <div className="space-y-2" data-testid="deals-list">
        {filtered.length === 0 && (
          <div className="glass-card-static p-12 text-center">
            <Briefcase className="w-12 h-12 text-white/10 mx-auto mb-3" />
            <p className="text-white/40">No deals found</p>
          </div>
        )}
        {filtered.map(deal => (
          <Link key={deal.id} to={`/deals/${deal.id}`} className="block" data-testid={`deal-row-${deal.id}`}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card-static p-4 flex items-center gap-4 hover:bg-white/[0.04] transition-colors cursor-pointer group">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-white font-medium truncate">{deal.title}</p>
                  <span className={`text-xs font-semibold ${PRIORITY_COLORS[deal.priority]}`}>{PRIORITY_LABELS[deal.priority]}</span>
                </div>
                <p className="text-white/40 text-sm truncate">{deal.company_name || "No company"}{deal.primary_contact_name ? ` · ${deal.primary_contact_name}` : ""}</p>
              </div>
      <span className={`px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap ${DEAL_STATUS_COLORS[deal.status]}`}>{DEAL_STATUS_LABELS[deal.status]}</span>
              {deal.value_estimate > 0 && <span className="font-mono text-white text-sm whitespace-nowrap">{"\u20AC"}{deal.value_estimate.toLocaleString()}</span>}
              {deal.next_action_date && <span className="hidden lg:flex items-center gap-1 text-white/30 text-xs whitespace-nowrap"><Calendar className="w-3 h-3" />{new Date(deal.next_action_date).toLocaleDateString()}</span>}
              <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-white/50 transition-colors" />
            </motion.div>
          </Link>
        ))}
      </div>

      {/* Create Panel */}
      <AnimatePresence>
        {showCreate && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={() => setShowCreate(false)} />
            <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }} className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-[#0A0A0A] border-l border-white/10 z-50 overflow-y-auto" data-testid="create-deal-panel">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-display text-xl text-white">New Deal</h2>
                  <button onClick={() => setShowCreate(false)} className="p-2 text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div><label className="form-label">Title *</label><input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="form-input" required placeholder="e.g. Hotel Alpenblick Partnership" data-testid="deal-title-input" /></div>
                  <div><label className="form-label">Company</label>
                    <select value={form.company_id} onChange={e => setForm(p => ({ ...p, company_id: e.target.value, primary_contact_id: "" }))} className="form-input" data-testid="deal-company">
                      <option value="">Select Company</option>
                      {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                    </select>
                  </div>
                  <div><label className="form-label">Primary Contact</label>
                    <select value={form.primary_contact_id} onChange={e => setForm(p => ({ ...p, primary_contact_id: e.target.value }))} className="form-input" data-testid="deal-contact">
                      <option value="">Select Contact</option>
                      {companyContacts.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="form-label">Status</label>
                      <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="form-input">
                        {DEAL_STATUSES.map(s => <option key={s} value={s}>{DEAL_STATUS_LABELS[s]}</option>)}
                      </select>
                    </div>
                    <div><label className="form-label">Priority</label>
                      <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} className="form-input">
                        {PRIORITIES.map(p => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
                      </select>
                    </div>
                  </div>
                  <div><label className="form-label">Value Estimate ({"\u20AC"})</label><input type="number" value={form.value_estimate} onChange={e => setForm(p => ({ ...p, value_estimate: e.target.value }))} className="form-input" data-testid="deal-value" /></div>
                  <div><label className="form-label">Source</label><input value={form.source} onChange={e => setForm(p => ({ ...p, source: e.target.value }))} className="form-input" placeholder="e.g. Website, Referral" /></div>
                  <div><label className="form-label">Next Action Date</label><input type="date" value={form.next_action_date} onChange={e => setForm(p => ({ ...p, next_action_date: e.target.value }))} className="form-input" /></div>
                  <div><label className="form-label">Notes</label><textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="form-input min-h-[80px]" /></div>
                  <button type="submit" disabled={saving} className="btn-accent w-full flex items-center justify-center gap-2" data-testid="save-deal-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create Deal
                  </button>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
